import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { itemPath, useOpenTarget } from "@/lib/deeplink";

// What each kind of pending item is called, and how its line reads.
const KINDS = [
    ["leaves", "leave", "Leave", (it) => `${it._employee_name || "Employee"}: ${it.leave_type || "leave"}, ${it.days ?? "?"} day(s)`],
    ["overtime", "overtime", "Overtime", (it) => `${it._employee_name || "Employee"}: ${it.hours ?? "?"}h on ${it.work_date || ""}`],
    ["attendance_corrections", "attendance", "Attendance correction", (it) => `${it._employee_name || "Employee"}: ${it.work_date || ""}`],
    ["payroll_runs", "payroll", "Payroll run", (it) => `${it.period_start} to ${it.period_end}: ${String(it.status || "").replaceAll("_", " ")}`],
    ["helpdesk", "helpdesk", "Ticket", (it) => `${it.subject || "Ticket"}${it._employee_name ? ` (${it._employee_name})` : ""}`],
    ["document_requests", "document", "Document request", (it) => `${it._employee_name || "Employee"}: ${it.doc_label || it.doc_type || "document"}`],
    ["time_adjustments", "time_adjustment", "Time correction", (it) => `${it._employee_name || "Employee"}: ${it.work_date || ""}`],
    ["schedule_adjustments", "schedule_adjustment", "Shift change", (it) => `${it._employee_name || "Employee"}`],
    ["final_pay", "final_pay", "Final pay", (it) => `${it._employee_name || "Employee"}`],
    ["candidates_offer", "candidate", "Offer stage", (it) => `${it.name || [it.first_name, it.last_name].filter(Boolean).join(" ") || "Candidate"}`],
];
const SHOWN = 8;

export default function NotificationsBell() {
    const navigate = useNavigate();
    const open = useOpenTarget();
    const { companies } = useAuth();
    const several = (companies || []).length > 1;
    const [pending, setPending] = useState([]);       // newest first
    const [pendingTotal, setPendingTotal] = useState(0);
    const [notes, setNotes] = useState([]);           // unread notifications
    const [unread, setUnread] = useState(0);
    const [show, setShow] = useState(false);
    const box = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/operations/approvals/pending", { params: { scope: several ? "all" : "company" } });
            const rows = [];
            let total = 0;
            for (const [key, kind, label, line] of KINDS) {
                for (const it of data[key] || []) {
                    rows.push({ id: it.id, kind, label, line: line(it), company: it._company_name, companyId: it._company_id,
                                at: it.created_at || it.submitted_at || it.requested_at || "", path: itemPath(kind, it) });
                    total += 1;
                }
            }
            rows.sort((a, b) => String(b.at).localeCompare(String(a.at)));
            setPending(rows); setPendingTotal(total);
        } catch { /* the bell must never break the page */ }
        try {
            const { data } = await api.get("/me/notifications");
            setNotes((data.items || []).filter((n) => !n.read).slice(0, 5)); setUnread(data.unread || 0);
        } catch { /* ignore */ }
    }, [several]);

    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        if (!show) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setShow(false); };
        document.addEventListener("mousedown", away);
        return () => document.removeEventListener("mousedown", away);
    }, [show]);

    const total = pendingTotal + unread;
    const go = (path, companyId) => { setShow(false); open(path, companyId); };
    const goNote = async (n) => {
        try { await api.post(`/me/notifications/${n.id}/read`); } catch { /* still go */ }
        go(n.link || "/me/notifications", n.company_id);
    };

    return (
        <div className="relative" ref={box}>
            <button
                onClick={() => { setShow((s) => !s); if (!show) load(); }}
                className="relative h-9 w-9 rounded-md border border-border bg-background hover:bg-secondary flex items-center justify-center"
                data-testid="notifications-btn"
                title={`${pendingTotal} waiting for a decision, ${unread} unread`}
                aria-haspopup="true" aria-expanded={show}
            >
                <Bell className="size-4 text-muted-foreground" />
                {total > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="bell-count">
                        {total > 99 ? "99+" : total}
                    </span>
                )}
            </button>
            {show && (
                <div className="absolute right-0 mt-2 w-[380px] max-w-[92vw] rounded-lg border border-border bg-background shadow-lg z-50" data-testid="bell-panel">
                    <div className="max-h-[70vh] overflow-auto">
                        {notes.length > 0 && (
                            <div>
                                <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Unread notifications</div>
                                {notes.map((n) => (
                                    <button key={n.id} onClick={() => goNote(n)} className="w-full text-left px-3 py-2 hover:bg-secondary/60 border-t border-border/60 first:border-t-0" data-testid={`bell-note-${n.id}`}>
                                        <div className="text-sm font-medium">{n.title}</div>
                                        {n.body && <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{formatDateTime(n.created_at)}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div>
                            <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Waiting for a decision ({pendingTotal})</div>
                            {!pending.length && <div className="px-3 pb-3 text-sm text-muted-foreground">Nothing is waiting.</div>}
                            {pending.slice(0, SHOWN).map((p) => (
                                <button key={`${p.kind}:${p.id}`} onClick={() => go(p.path, p.companyId)} className="w-full text-left px-3 py-2 hover:bg-secondary/60 border-t border-border/60" data-testid={`bell-item-${p.kind}-${p.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-semibold text-primary">{p.label}</span>
                                        {several && p.company && <span className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">{p.company}</span>}
                                    </div>
                                    <div className="text-sm">{p.line}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
                        <button className="text-primary hover:underline" onClick={() => { setShow(false); navigate("/approvals"); }} data-testid="bell-open-inbox">
                            {pendingTotal > SHOWN ? `See all ${pendingTotal} in the Approvals Inbox` : "Open the Approvals Inbox"}
                        </button>
                        <button className="text-muted-foreground hover:underline" onClick={() => { setShow(false); navigate("/me/notifications"); }}>All notifications</button>
                    </div>
                </div>
            )}
        </div>
    );
}
